import React, { useEffect, useState, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Card, TextInput, SelectInput, btnPrimary, btnSecondary, StatusBadge, fmtDate, inputCls } from "@/components/common/Form";
import { LoadingState, EmptyState } from "@/components/common/States";

export const can = (user, p) => user?.role === "admin" || (user?.permissions || []).includes(p);

export function Pager({ data, onPage }) {
  if (!data || data.pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 text-sm mt-4" data-testid="pager">
      <button disabled={data.page <= 1} onClick={() => onPage(data.page - 1)} className={btnSecondary}><ChevronLeft size={14} /></button>
      <span className="text-stone-500">Page {data.page} of {data.pages}</span>
      <button disabled={data.page >= data.pages} onClick={() => onPage(data.page + 1)} className={btnSecondary}><ChevronRight size={14} /></button>
    </div>
  );
}

const ROLE_OPTS = [["", "All roles"], ["customer", "Customer"], ["business_buyer", "Business Buyer"], ["vendor", "Vendor"], ["staff", "Staff"], ["admin", "Admin"]];
const STATUS_OPTS = [["", "All statuses"], ["ACTIVE", "Active"], ["INACTIVE", "Inactive"], ["SUSPENDED", "Suspended"], ["PENDING_VERIFICATION", "Pending verification"], ["BLOCKED", "Blocked"]];

export default function AdminUsers() {
  const { user: me } = useStore();
  const [filters, setFilters] = useState({ q: "", role: "", status: "", verified: "", page: 1 });
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v !== ""));
    api.get(`/admin/users?${p}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value, page: 1 });

  return (
    <div className="space-y-6">
      <Card title="Users" subtitle="Customers, business buyers, vendors and internal accounts." testId="admin-users">
        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          <div className="relative sm:col-span-1"><Search size={15} className="absolute left-3 top-3.5 text-stone-400" /><input data-testid="users-search" value={filters.q} onChange={set("q")} placeholder="Search email, name, phone" className={`${inputCls} pl-9 mt-0`} /></div>
          <SelectInput testId="users-role-filter" value={filters.role} onChange={set("role")} options={ROLE_OPTS} />
          <SelectInput testId="users-status-filter" value={filters.status} onChange={set("status")} options={STATUS_OPTS} />
          <SelectInput testId="users-verified-filter" value={filters.verified} onChange={set("verified")} options={[["", "Any verification"], ["email", "Email verified"], ["unverified", "Email unverified"]]} />
        </div>
        {!data ? <LoadingState /> : data.items.length === 0 ? <EmptyState title="No users match" /> : (
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm min-w-[720px]" data-testid="users-table">
              <thead><tr className="text-left text-xs text-stone-500 border-b border-border"><th className="py-2 px-3">User</th><th className="px-3">Role</th><th className="px-3">Status</th><th className="px-3">Verified</th><th className="px-3">Joined</th><th className="px-3">Last login</th><th></th></tr></thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/60 hover:bg-muted/50">
                    <td className="py-2.5 px-3"><div className="font-medium text-stone-900">{u.name}</div><div className="text-xs text-stone-500">{u.email}</div></td>
                    <td className="px-3 capitalize">{u.role.replace("_", " ")}</td>
                    <td className="px-3"><StatusBadge value={u.status} /></td>
                    <td className="px-3 text-xs">{u.email_verified ? "Email ✓" : "—"}{u.phone_verified ? " · Phone ✓" : ""}</td>
                    <td className="px-3 text-xs text-stone-500">{fmtDate(u.created_at)}</td>
                    <td className="px-3 text-xs text-stone-500">{fmtDate(u.last_login_at)}</td>
                    <td className="px-3"><button data-testid={`user-view-${u.id}`} onClick={() => setSelected(u.id)} className="text-xs font-semibold text-secondary">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager data={data} onPage={(page) => setFilters({ ...filters, page })} />
      </Card>
      {selected && <UserDetail id={selected} me={me} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function UserDetail({ id, me, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get(`/admin/users/${id}`).then((r) => { setD(r.data); setStatus(r.data.user.status); setRole(r.data.user.role); }), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Card testId="user-detail"><LoadingState /></Card>;
  const u = d.user;
  const act = async (path, body, ok) => {
    try { await api.post(`/admin/users/${id}/${path}`, body); toast.success(ok); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title={u.name} subtitle={u.email} testId="user-detail" action={<button onClick={onClose} data-testid="user-detail-close" className={btnSecondary}>Close</button>}>
      <div className="grid md:grid-cols-2 gap-6 text-sm">
        <div className="space-y-3">
          <dl className="grid grid-cols-2 gap-2">
            <dt className="text-stone-500">Role</dt><dd className="capitalize">{u.role.replace("_", " ")}</dd>
            <dt className="text-stone-500">Status</dt><dd><StatusBadge value={u.status} testId="detail-status" /></dd>
            <dt className="text-stone-500">Phone</dt><dd>{u.phone || "—"} {u.phone_verified && "✓"}</dd>
            <dt className="text-stone-500">Email verified</dt><dd>{u.email_verified ? "Yes" : "No"}</dd>
            <dt className="text-stone-500">Joined</dt><dd>{fmtDate(u.created_at)}</dd>
            <dt className="text-stone-500">Last login</dt><dd>{fmtDate(u.last_login_at)}</dd>
            <dt className="text-stone-500">Active sessions</dt><dd>{d.sessions}</dd>
          </dl>
          {d.business && <div className="rounded-lg bg-muted p-3"><div className="font-semibold mb-1">Business</div>{d.business.business_name || "—"} · GST {d.business.gst_number || "—"} <StatusBadge value={d.business.verification_status} /></div>}
          {d.vendor && <div className="rounded-lg bg-muted p-3"><div className="font-semibold mb-1">Vendor</div>{d.vendor.business_name} <StatusBadge value={d.vendor.status} /> <StatusBadge value={d.vendor.verification_status} /></div>}
          {d.addresses?.length > 0 && <div><div className="font-semibold mb-1">Addresses ({d.addresses.length})</div><ul className="text-xs text-stone-600 space-y-1">{d.addresses.map((a) => <li key={a.id}>{a.address_type}: {a.address_line_1}, {a.city}, {a.state} {a.postal_code}</li>)}</ul></div>}
        </div>
        <div className="space-y-4">
          {can(me, "users.suspend") && u.id !== me.id && (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="font-semibold">Account status</div>
              <SelectInput testId="detail-status-select" value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_OPTS.slice(1)} />
              <TextInput testId="detail-reason" placeholder="Reason (recorded in audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button data-testid="detail-status-save" onClick={() => act("status", { status, reason }, "Status updated")} className={btnPrimary}>Update status</button>
            </div>
          )}
          {can(me, "users.roles") && u.id !== me.id && (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="font-semibold">Role</div>
              <SelectInput testId="detail-role-select" value={role} onChange={(e) => setRole(e.target.value)} options={ROLE_OPTS.slice(1).filter(([v]) => v !== "admin" || me.role === "admin")} />
              <button data-testid="detail-role-save" onClick={() => act("role", { role, reason }, "Role updated")} className={btnPrimary}>Change role</button>
            </div>
          )}
          {d.audit && (
            <div><div className="font-semibold mb-1">Recent activity</div>
              <ul className="text-xs text-stone-600 space-y-1 max-h-56 overflow-auto" data-testid="detail-audit">{d.audit.map((l) => <li key={l.id}><span className="font-semibold text-stone-800">{l.action}</span> · {l.target_type} · {fmtDate(l.created_at)}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
